import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CheckCircle2, Circle, Bell } from "lucide-react";
import { api, fmtDate, todayISO } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const empty = { title: "", car_id: "", description: "", due_date: todayISO(), notify_days_before: 7 };

export default function Reminders() {
    const { isAdmin, canWrite } = useAuth();
    const [items, setItems] = useState([]);
    const [cars, setCars] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = async () => {
        const [a, b, n] = await Promise.all([
            api.get("/reminders"),
            api.get("/cars"),
            api.get("/notifications"),
        ]);
        setItems(a.data); setCars(b.data); setNotifications(n.data);
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (r) => {
        setEditing(r);
        setForm({
            title: r.title, car_id: r.car_id || "", description: r.description || "",
            due_date: r.due_date, notify_days_before: r.notify_days_before,
        });
        setOpen(true);
    };
    const submit = async () => {
        if (!form.title || !form.due_date) return toast.error("Título y fecha requeridos");
        const payload = {
            ...form,
            car_id: form.car_id || null,
            notify_days_before: Number(form.notify_days_before) || 0,
        };
        try {
            if (editing) await api.put(`/reminders/${editing.id}`, payload);
            else await api.post("/reminders", payload);
            toast.success("Guardado");
            setOpen(false); load();
        } catch { toast.error("Error"); }
    };
    const toggle = async (r) => {
        await api.put(`/reminders/${r.id}`, { completed: !r.completed });
        load();
    };
    const remove = async (r) => {
        if (!window.confirm("¿Eliminar recordatorio?")) return;
        await api.delete(`/reminders/${r.id}`);
        toast.success("Eliminado"); load();
    };

    const today = new Date();
    const dayDiff = (d) => Math.ceil((new Date(d) - today) / (1000*60*60*24));

    return (
        <div className="space-y-6" data-testid="reminders-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Notificaciones</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1 font-display">Recordatorios</h1>
                    <p className="text-sm text-slate-500 mt-1">Configura avisos para mantenimiento, verificación, pagos y seguros.</p>
                </div>
                {canWrite && (
                    <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-reminder-button">
                        <Plus size={16} className="mr-1" /> Nuevo recordatorio
                    </Button>
                )}
            </header>

            {notifications.length > 0 && (
                <Card className="border-amber-200 bg-amber-50 shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-lg font-display font-medium text-amber-900 flex items-center gap-2">
                            <Bell size={18} /> Próximas alertas ({notifications.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {notifications.map((n) => (
                            <div key={`${n.kind}-${n.id}`} className="flex items-center justify-between p-3 rounded-md bg-white border border-amber-200">
                                <div>
                                    <div className="text-sm font-medium text-slate-900">{n.title}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{n.description || (n.kind === "insurance" ? "Vencimiento de póliza" : "")}</div>
                                </div>
                                <div className="text-right">
                                    <Badge className={`${n.days_left < 0 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"} hover:${n.days_left < 0 ? "bg-rose-100" : "bg-amber-100"}`}>
                                        {n.days_left < 0 ? `Vencido hace ${Math.abs(n.days_left)} d` : `En ${n.days_left} d`}
                                    </Badge>
                                    <div className="text-[11px] text-slate-500 mt-1">{fmtDate(n.due_date)}</div>
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-lg font-display font-medium">Todos los recordatorios</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="px-4 py-3" />
                                    <th className="text-left px-4 py-3 font-semibold">Título</th>
                                    <th className="text-left px-4 py-3 font-semibold">Auto</th>
                                    <th className="text-left px-4 py-3 font-semibold">Vencimiento</th>
                                    <th className="text-left px-4 py-3 font-semibold">Avisar antes</th>
                                    <th className="text-left px-4 py-3 font-semibold">Estado</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 && <tr><td colSpan={7} className="text-center px-4 py-10 text-slate-400">Sin recordatorios</td></tr>}
                                {items.map((r) => {
                                    const car = cars.find((c) => c.id === r.car_id);
                                    const d = dayDiff(r.due_date);
                                    return (
                                        <tr key={r.id} className={`border-t border-slate-100 ${r.completed ? "opacity-50" : ""}`} data-testid={`reminder-${r.id}`}>
                                            <td className="px-4 py-3">
                                                <button onClick={() => canWrite && toggle(r)} disabled={!canWrite} data-testid={`toggle-${r.id}`}>
                                                    {r.completed ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Circle size={18} className="text-slate-400" />}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className={`font-medium text-slate-900 ${r.completed ? "line-through" : ""}`}>{r.title}</div>
                                                {r.description && <div className="text-xs text-slate-500 mt-0.5">{r.description}</div>}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">{car?.plate || "—"}</td>
                                            <td className="px-4 py-3 text-slate-700">{fmtDate(r.due_date)}</td>
                                            <td className="px-4 py-3 text-slate-700">{r.notify_days_before} d</td>
                                            <td className="px-4 py-3">
                                                {r.completed
                                                    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Hecho</Badge>
                                                    : d < 0 ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Vencido</Badge>
                                                    : d <= r.notify_days_before ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Próximo</Badge>
                                                    : <Badge variant="secondary">Pendiente</Badge>}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-1">
                                                    {canWrite && (
                                                        <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(r)} data-testid={`edit-rem-${r.id}`}><Pencil size={14} /></button>
                                                    )}
                                                    {isAdmin && (
                                                        <button className="p-1.5 rounded hover:bg-rose-50 text-rose-600" onClick={() => remove(r)} data-testid={`del-rem-${r.id}`}><Trash2 size={14} /></button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Editar recordatorio" : "Nuevo recordatorio"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <Label className="text-xs">Título</Label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="rem-title" />
                        </div>
                        <div>
                            <Label className="text-xs">Auto (opcional)</Label>
                            <Select value={form.car_id || "none"} onValueChange={(v) => setForm({ ...form, car_id: v === "none" ? "" : v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Sin auto</SelectItem>
                                    {cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.plate} · {c.brand}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Fecha</Label>
                            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="rem-date" />
                        </div>
                        <div>
                            <Label className="text-xs">Días para avisar antes</Label>
                            <Input type="number" min="0" value={form.notify_days_before} onChange={(e) => setForm({ ...form, notify_days_before: e.target.value })} data-testid="rem-notify-days" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Descripción</Label>
                            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={submit} data-testid="save-reminder">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
